import * as React from 'react';
import { GoDecision, ProjectType, WorkModel, type EnumDef } from '@clawhire/shared';
import { formatDateTime } from '@/lib/format';
import { PropertiesPanel, type PropertyField } from '@/components/data/PropertiesPanel';
import { SectionCard } from '../components/SectionCard';
import { ownerOptions, useOptions, usePartners, useUsersDirectory } from '../queries';
import type { PositionDetail } from '../types';

export interface PositionPropertiesProps {
  position: PositionDetail;
  canEdit: boolean;
  onPatch: (patch: Record<string, unknown>) => Promise<unknown>;
}

function withCurrent(values: readonly string[], current: string | null): { value: string; label: string }[] {
  const list = current && !values.includes(current) ? [current, ...values] : [...values];
  return list.map((v) => ({ value: v, label: v }));
}

/** Inline-editable properties → `PATCH /positions/:id`. */
export function PositionProperties({ position, canEdit, onPatch }: PositionPropertiesProps) {
  const intermediaries = usePartners('INTERMEDIARY');
  const users = useUsersDirectory();
  const options = useOptions();

  const fields = React.useMemo<PropertyField[]>(() => {
    const partnerOptions = (intermediaries.data ?? []).map((p) => ({ value: p.id, label: p.name }));
    if (position.intermediary && !partnerOptions.some((o) => o.value === position.intermediary?.id)) {
      partnerOptions.unshift({ value: position.intermediary.id, label: position.intermediary.name });
    }
    return [
      {
        key: 'intermediary',
        label: 'Intermediary',
        type: 'select',
        value: position.intermediaryId,
        options: partnerOptions,
        allowEmpty: true,
      },
      {
        key: 'ownerId',
        label: 'Owner',
        type: 'select',
        value: position.ownerId,
        options: ownerOptions(users.data, position.owner),
        allowEmpty: true,
        help: 'The team member responsible for this role',
      },
      {
        key: 'department',
        label: 'Department',
        type: 'select',
        value: position.department,
        options: withCurrent(options.data?.department ?? [], position.department),
        allowEmpty: true,
      },
      {
        key: 'workModel',
        label: 'Work model',
        type: 'enum',
        value: position.workModel,
        def: WorkModel as EnumDef<string>,
        allowEmpty: true,
        help: 'On-site positions are not taken on (spec §6.1)',
      },
      { key: 'languages', label: 'Languages', type: 'list', value: position.languages },
      { key: 'startDate', label: 'Start', type: 'date', value: position.startDate },
      { key: 'endDate', label: 'End', type: 'date', value: position.endDate },
      {
        key: 'submissionDeadline',
        label: 'Submission deadline',
        type: 'date',
        value: position.submissionDeadline,
      },
      {
        key: 'goDecision',
        label: 'Go decision',
        type: 'enum',
        value: position.goDecision,
        def: GoDecision as EnumDef<string>,
      },
      { key: 'goReason', label: 'Go reason', type: 'text', value: position.goReason },
      {
        key: 'projectType',
        label: 'Project type',
        type: 'enum',
        value: position.projectType,
        def: ProjectType as EnumDef<string>,
        allowEmpty: true,
      },
      {
        key: 'workload',
        label: 'Workload',
        type: 'text',
        value: position.workload,
        placeholder: 'e.g. 100 %',
      },
      { key: 'location', label: 'Location', type: 'text', value: position.location },
      {
        key: 'clientInternalName',
        label: 'Client (internal)',
        type: 'text',
        value: position.clientInternalName,
        help: 'Internal only – never shown on documents or to suppliers',
      },
      { key: 'seats', label: 'Seats', type: 'number', value: position.seats, min: 1, step: 1 },
      {
        key: 'jdReference',
        label: 'JD reference',
        type: 'text',
        value: position.jdReference,
        editable: false,
      },
      {
        key: 'created',
        label: 'Created',
        type: 'custom',
        render: () => (
          <span className="text-muted-foreground">
            {formatDateTime(position.createdAt)}
            {position.createdBy ? ` · ${position.createdBy}` : ''}
          </span>
        ),
      },
    ];
  }, [position, intermediaries.data, users.data, options.data]);

  return (
    <SectionCard
      title="Properties"
      description="Click a value to edit it. Changes are saved on Enter or when the field loses focus."
    >
      <PropertiesPanel fields={fields} onSave={onPatch} readOnly={!canEdit} />
    </SectionCard>
  );
}
